import { useCallback, useEffect, useRef } from "react"

import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"

import type { SimulationTemplateData } from "./types"

const log = logger("EvaluationLauncher")

export function useAgentSelectionChange(
  selectedAgentId: string | undefined,
  currentAgentIds: string[] | undefined,
  updateTemplateData: (updates: Partial<SimulationTemplateData>) => void,
  isSelectionPending: boolean,
) {
  const queryParams = useQueryParams()
  const clearedAgentIdRef = useRef<string | undefined>(undefined)
  const clearStaleAgent = useCallback(
    (agentIds: string[]) => {
      if (
        selectedAgentId &&
        clearedAgentIdRef.current !== selectedAgentId &&
        (agentIds.length !== 1 || agentIds[0] !== selectedAgentId)
      ) {
        clearedAgentIdRef.current = selectedAgentId
        queryParams.setMultiple(
          { agent_id: null },
          { history: "replace", shallow: true },
        )
      }
    },
    [queryParams, selectedAgentId],
  )

  useEffect(() => {
    if (!selectedAgentId) {
      clearedAgentIdRef.current = undefined
    } else if (!isSelectionPending && currentAgentIds?.length) {
      clearStaleAgent(currentAgentIds)
    }
  }, [clearStaleAgent, currentAgentIds, isSelectionPending, selectedAgentId])

  return useCallback(
    (agentIds: string[]) => {
      updateTemplateData({ agentIds, mutationIdsByAgent: {} })
      clearStaleAgent(agentIds)
    },
    [clearStaleAgent, updateTemplateData],
  )
}

export async function copyRunIds(runIds: string[]) {
  try {
    await navigator.clipboard.writeText(runIds.join(", "))
    toast.success({
      title: "Copied!",
      description: `Run ID${runIds.length > 1 ? "s" : ""} copied to clipboard`,
      duration: 2000,
    })
  } catch (error) {
    log.error("Failed to copy run IDs", error)
    toast.danger({
      title: "Copy failed",
      description: "Unable to copy run IDs to the clipboard.",
      duration: 3000,
    })
  }
}
